import React, { useCallback, useMemo } from "react";

import { css } from "@emotion/css";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useReactTable, createColumnHelper, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";

import { useSelectPatientsListColumns } from "@/hooks/useSelectPatientsListColumns.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { PatientListItem, type PatientList } from "@/models/PatientSummaryModel.ts";
import type { Profession } from "@/models/ProfessionModel.ts";
import type { PatientListColumnId } from "@/models/UISettingsModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { PatientRenewal } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx";
import { DiabetesTypeColumn, DiabetesTypeHeader, sortByDiabetesType } from "@/uiKit/organisms/PatientsList/columns/DiabetesTypeColumn.tsx";
import { DoctorMessagesColumn, DoctorMessagesHeader, sortByDoctorUnreadMessages } from "@/uiKit/organisms/PatientsList/columns/DoctorMessagesColumn.tsx";
import { DoctorPatientNameColumn, DoctorPatientNameHeader } from "@/uiKit/organisms/PatientsList/columns/DoctorPatientNameColumn.tsx";
import { LastSeenColumn, LastSeenHeader, sortByLastSeen } from "@/uiKit/organisms/PatientsList/columns/LastSeenColumn.tsx";
import { NurseNoticeColumn, NoticeHeader, DoctorNoticeColumn, sortByNurseNoticeForNurse, sortByNurseNoticeForDoctor } from "@/uiKit/organisms/PatientsList/columns/NoticeColumn.tsx";
import { NurseMessagesColumn, NurseMessagesHeader, sortByNurseUnreadMessages } from "@/uiKit/organisms/PatientsList/columns/NurseMessagesColumn.tsx";
import { NursePatientNameColumn, NursePatientNameHeader } from "@/uiKit/organisms/PatientsList/columns/NursePatientNameColumn.tsx";
import { ReferentDoctorColumn, ReferentDoctorHeader, sortByDoctorName } from "@/uiKit/organisms/PatientsList/columns/ReferentDoctorColumn.tsx";
import { TelemonitoringColumn, TelemonitoringHeader, sortByTelemonitoringTag } from "@/uiKit/organisms/PatientsList/columns/TelemonitoringColumn.tsx";
import { sortByNurseName, TelemonitoringNurseColumn, TelemonitoringNurseHeader } from "@/uiKit/organisms/PatientsList/columns/TelemonitoringNurseColumn.tsx";
import { PatientListColumnVisibilityMenu } from "@/uiKit/organisms/PatientsList/PatientListColumnVisibilityMenu.tsx";
import type { PatientListMenuItems } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";
import { PatientListRenewalProvider, usePatientListRenewal } from "@/uiKit/organisms/PatientsList/PatientListRenewalState.ts";
import { MuiTable } from "@/uiKit/Table.tsx";
import { HoveredRowFeature } from "@/utils/table/RowHoverFeature.ts";

export type PatientListTableProps = {
  id: string;
  qualification: Profession;
  nurseHasMultipleDoctors?: boolean;
  tab: PatientListMenuItems;
  data: PatientList;
};

export const PATIENT_LIST_DEFAULT_COLUMN_SIZE = 15;

export const columnHelper = createColumnHelper<PatientListItem>();

export const patientListColumns = {
  patientNameForNurse: columnHelper.accessor("familyName", {
    id: "patientNameForNurse",
    cell: NursePatientNameColumn,
    header: NursePatientNameHeader,
    enableSorting: false,
  }),
  patientNameForDoctor: columnHelper.accessor("familyName", {
    id: "patientNameForDoctor",
    cell: DoctorPatientNameColumn,
    header: DoctorPatientNameHeader,
    enableSorting: false,
  }),
  diabetes: columnHelper.accessor("diabetesType", {
    id: "diabetes",
    cell: DiabetesTypeColumn,
    header: DiabetesTypeHeader,
    sortingFn: sortByDiabetesType,
  }),
  telemonitoring: columnHelper.accessor("tags", {
    id: "telemonitoring",
    cell: TelemonitoringColumn,
    header: TelemonitoringHeader,
    sortingFn: sortByTelemonitoringTag,
  }),
  noticesForNurse: columnHelper.accessor("hasNurseNotice", {
    id: "noticesForNurse",
    cell: NurseNoticeColumn,
    header: NoticeHeader,
    sortingFn: sortByNurseNoticeForNurse,
  }),
  noticesForDoctor: columnHelper.accessor("hasNurseNotice", {
    id: "noticesForDoctor",
    cell: DoctorNoticeColumn,
    header: NoticeHeader,
    sortingFn: sortByNurseNoticeForDoctor,
  }),
  messagesForNurse: columnHelper.accessor("unreadMessages", {
    id: "messagesForNurse",
    cell: NurseMessagesColumn,
    header: NurseMessagesHeader,
    sortingFn: sortByNurseUnreadMessages,
  }),
  messagesForDoctor: columnHelper.accessor("unreadMessages", {
    id: "messagesForDoctor",
    cell: DoctorMessagesColumn,
    header: DoctorMessagesHeader,
    sortingFn: sortByDoctorUnreadMessages,
  }),
  referentDoctor: columnHelper.accessor("doctor.id", {
    id: "referentDoctor",
    cell: ReferentDoctorColumn,
    header: ReferentDoctorHeader,
    sortingFn: sortByDoctorName,
  }),
  tlsNurse: columnHelper.accessor("telemonitoringNurse.id", {
    id: "tlsNurse",
    cell: TelemonitoringNurseColumn,
    header: TelemonitoringNurseHeader,
    sortingFn: sortByNurseName,
  }),
  lastSeen: columnHelper.accessor("patientLastSeen", {
    id: "lastSeen",
    cell: LastSeenColumn,
    header: LastSeenHeader,
    sortingFn: sortByLastSeen,
  }),
} satisfies Record<PatientListColumnId, unknown>;

export const PatientListTable: React.FC<PatientListTableProps> = ({
  id,
  data,
  qualification,
  nurseHasMultipleDoctors,
  tab,
}) => {
  const styles = useStyles(makeStyles);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { displayedColumns, availableColumns, visibilityState } = useSelectPatientsListColumns({
    qualification,
    hasMultipleDoctors: nurseHasMultipleDoctors,
    tab,
  });
  const sorting = Queries.user.usePatientListSort({});
  const updateSorting = Queries.user.useUpdatePatientListSort();

  const sizedColumns = useMemo(() => {
    const visibleCount = displayedColumns.length;
    const firstColumnSize = Math.max(
      PATIENT_LIST_DEFAULT_COLUMN_SIZE,
      100 - Math.max(0, visibleCount - 1) * PATIENT_LIST_DEFAULT_COLUMN_SIZE,
    );
    let visibleIdx = 0;
    return availableColumns.map(colId => {
      const col = patientListColumns[colId];
      col.size = visibleIdx === 0 ? firstColumnSize : PATIENT_LIST_DEFAULT_COLUMN_SIZE;
      visibleIdx += 1;
      return col;
    });
  }, [displayedColumns, availableColumns]);

  const table = useReactTable<PatientListItem>({
    _features: [HoveredRowFeature],
    data,
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: 100,
    },
    columns: sizedColumns,
    state: {
      columnVisibility: visibilityState,
      sorting,
    },
    onColumnVisibilityChange: () => {},
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => row.id,
    enableRowSelection: false,
    maxMultiSortColCount: 1,
    onSortingChange: sort => {
      const data = typeof sort === "function" ? sort(sorting) : sort;
      updateSorting.mutate({ sorting: data });
    },
  });

  const handleClick = useCallback(
    (patient: PatientListItem) => {
      Queries.messaging.useMessages.invalidate(queryClient, {
        patientId: patient.id,
      });
      navigate({
        to: `/patients/${patient.id}`,
      });
    },
    [navigate, queryClient],
  );

  return (
    <div className={styles.container}>
      <PatientListRenewalProvider>
        <MuiTable
          id={id}
          table={table}
          handleRowClick={handleClick}
          getRowAttributes={patient => ({
            "data-testid": "patient-row",
            "data-patient-id": patient.id,
          })}
          classes={{
            row: "patient-list-row",
            cell: styles.cell,
          }}
        />
        <PatientListColumnVisibilityMenu
          id="doctor-patient-list-column-visibility"
          className={styles.columnVisibilityMenu}
          qualification={qualification}
          tab={tab}
        />
        <PatientRenewalContent />
      </PatientListRenewalProvider>
    </div>
  );
};

const PatientRenewalContent = () => {
  const { patientId, renewalType, closeRenewal } = usePatientListRenewal(state => state);
  if (!patientId || !renewalType) {
    return null;
  }
  return (
    <PatientRenewal
      patientId={patientId}
      renewalType={renewalType}
      open={true}
      onClose={() => closeRenewal()}
      onSubmit={() => closeRenewal()}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    position: "relative",
    height: "fit-content",
    width: "100%",
    overflow: "visible",
  }),
  cell: css({
    height: theme.spacing(26),
    padding: 0,
    minWidth: 0,
  }),
  columnVisibilityMenu: css({
    position: "absolute",
    right: 0,
    top: theme.spacing(-8),
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
  }),
});
